import {Component, OnInit, ViewChild} from '@angular/core';
import languageName from "../const/languageNames.json";
import {ActivatedRoute} from "@angular/router";
import {MatTree, MatTreeNestedDataSource} from "@angular/material/tree";
import {NestedTreeControl} from "@angular/cdk/tree";
import {EMPTY, expand, from, last, Observable} from "rxjs";
import {map, mergeMap, tap} from "rxjs/operators";
import {DataCatalogueService} from "../services/data-catalogue.service";
import languageNames from "../const/languageNames.json";
import {FormArray, FormBuilder, FormControl, FormGroup} from "@angular/forms";

interface IFeatureOption {
    label: string;
    value: string;
}

interface TreeNode {
    name: string;
    children?: TreeNode[];
    expanded?: boolean;
}

@Component({
    selector: 'property-group-view',
    templateUrl: './property-group-view.component.html',
    styleUrls: ['./property-group-view.component.css']
})
export class PropertyGroupViewComponent implements OnInit {
    featureName: string;
    propertyGroupName: string;
    metadataExpanded: boolean = false;
    @ViewChild(MatTree) tree: MatTree<TreeNode>;
    dataSource = new MatTreeNestedDataSource<TreeNode>();
    treeControl = new NestedTreeControl<TreeNode>(node => node.children);
    catId: string = '';
    metaData: any;
    form = new FormGroup({
        displayName: new FormArray([]),
        description: new FormArray([])
    });

    constructor(private route: ActivatedRoute, private dataCatalogueService: DataCatalogueService, private fb: FormBuilder,) {}

    initArrays() {
        this.metaData.displayName.forEach(item => {
            this.displayNameArray.push(
                new FormGroup({
                    lang: new FormControl({ value: item.lang, disabled: true }),
                    text: new FormControl({ value: item.text, disabled: true })
                })
            );
        });

        this.metaData.description.forEach(item => {
            this.descriptionArray.push(
                new FormGroup({
                    lang: new FormControl({ value: item.lang, disabled: true }),
                    text: new FormControl({ value: item.text, disabled: true })
                })
            );
        });
    }

    get displayNameArray() {
        return this.form.get('displayName') as FormArray;
    }

    get descriptionArray() {
        return this.form.get('description') as FormArray;
    }

    toggleMetadata(): void {
        this.metadataExpanded = !this.metadataExpanded;
    }

    ngAfterViewInit() {
        this.expandAll();
    }

    expandAll() {
        this.dataSource.data?.forEach(node => this.expandNodeRecursive(node, 1));
    }

    expandNodeRecursive(node: TreeNode, level: number) {
        if (node.children && level <= 2) {
            this.treeControl.expand(node);
            node.children.forEach(child => this.expandNodeRecursive(child, level + 1));
        }
    }

    childrenAccessor = (node: any) => node.children;
    hasChild = (_: number, node: any) => !!node.children && node.children.length > 0;

    mapCatalogDtoToNode(dto: any): TreeNode {
        return {
            name: dto?.name,
            children: []
        };
    }

    getLanguageName(code: string) : string {
        if (!!languageNames[code]) {
            return languageNames[code];
        } else return '';
    }

    fetchTreeData = (itemId: string): Observable<any> => {
        let root: any;

        return this.dataCatalogueService.getItemMetadata(this.catId, 'groups', 'aixm', itemId).pipe(
            map((dto) => {
                let metadata = dto?.versions?.[0];
                const updatedDate = new Date(metadata?.timestamp);
                metadata.timestamp = updatedDate?.toLocaleDateString('en-GB', {
                    day:   'numeric',
                    month: 'short',
                    year:  'numeric'
                });
                this.metaData = metadata;
                root = this.mapCatalogDtoToNode(dto);
                if (root?.name?.includes(":")) root.name = root?.name?.split(":").pop();
                root.type = 'groups';
                root.root = true;
                return root;
            }),
            expand((parent: any) => {

                const name = parent?.name?.split(":")?.pop() || parent?.name;
                if (parent?.type === 'property') return EMPTY;
                if (parent?.type === 'choice') return EMPTY;
                return this.dataCatalogueService.getItemMetadata(this.catId, parent?.type ? parent?.type : 'groups', 'aixm', name).pipe(
                    map((dto: any) =>
                        (dto?.versions?.[0]?.content ?? []).map((c) => {
                            const contentModelEntryType = c?.contentModelEntryType;
                            const catalogItemType = c?.catalogItemType;
                            const resolvedType = contentModelEntryType ? contentModelEntryType : catalogItemType ? catalogItemType : 'groups';
                            const type =
                                resolvedType.includes('propertyGroup')
                                    ? 'groups'
                                    : resolvedType.includes('feature')
                                        ? 'features'
                                        : resolvedType.includes('choice')
                                            ? 'choice'
                                            : 'property';
                            const name =
                                c?.name?.split(':')?.[1] ??
                                c?.ref?.split(':')?.[1];
                            if (type === 'property') {
                                return { name, type, propertyId: c?.id };
                            }
                            if (type === 'choice') {
                                const options = [];
                                c?.options?.forEach((option) => {
                                    const subOptions = [];
                                    if (option?.choiceOptionType === 'choice' && option?.options?.length > 0) {
                                        option?.options?.forEach((subOption) => {
                                            subOptions.push({
                                                name: subOption?.name?.split('_')?.[1],
                                                id: subOption?.id,
                                                propertyId: subOption?.id
                                            });
                                        });
                                        options.push({
                                            type: 'choice',
                                            options: subOptions
                                        });
                                    } else {
                                        options.push({
                                            name: option?.name?.split('_')?.[1],
                                            id: option?.id,
                                            propertyId: option?.id
                                        });
                                    }
                                });

                                return { type, options }
                            }

                            return { name, type, children: [] };
                        })
                    ),
                    tap(children => parent.children.push(...children)),
                    mergeMap(children => from(children))
                )
            }),
            last(),
            map(() => root)
        );
    }

    ngOnInit() {
        this.propertyGroupName = this.route.snapshot.paramMap.get('propertyGroupId');
        this.catId = this.route.snapshot.paramMap.get("catId");

        this.route.paramMap.subscribe(params => {
            this.propertyGroupName = params.get('propertyGroupId');
            this.catId = params.get("catId");
        });

        this.fetchTreeData(this.propertyGroupName).subscribe((res) => {
            this.dataSource.data = [res];
            this.expandAll();
            this.initArrays();
        });
    }
}